/**
 * Adaptador de tipos para MapViewer - V05.I
 * Convierte ProcessedInfrastructure a NormalizedInfrastructure
 * 
 * PTEL - Sistema de Normalización de Coordenadas
 */

import type { ProcessedInfrastructure } from '../../../types/processing';
import type { NormalizedInfrastructure, InfrastructureType } from '../../../types/infrastructure';
import { avisoCentroide, deriveProcedencia } from '../../../utils/procedenciaVia';

/**
 * Mapeo de tipos string a InfrastructureType enum
 */
const TYPE_MAP: Record<string, InfrastructureType> = {
  'SANITARIO': 'HEALTH' as InfrastructureType,
  'EDUCATIVO': 'EDUCATION' as InfrastructureType,
  'POLICIAL': 'POLICE' as InfrastructureType,
  'BOMBEROS': 'FIRE_STATION' as InfrastructureType,
  'CULTURAL': 'CULTURAL' as InfrastructureType,
  'RELIGIOSO': 'RELIGIOUS' as InfrastructureType,
  'DEPORTIVO': 'SPORTS' as InfrastructureType,
  'MUNICIPAL': 'MUNICIPAL' as InfrastructureType,
  'SOCIAL': 'SOCIAL' as InfrastructureType,
  'RESIDENCIAL': 'RESIDENTIAL' as InfrastructureType,
  'TRANSPORTE': 'TRANSPORT' as InfrastructureType,
  'ENERGIA': 'ENERGY' as InfrastructureType,
  'AGUA': 'WATER' as InfrastructureType,
  'TELECOMUNICACIONES': 'TELECOM' as InfrastructureType,
  'INDUSTRIAL': 'INDUSTRIAL' as InfrastructureType,
  'COMERCIAL': 'COMMERCIAL' as InfrastructureType,
  'OTRO': 'OTHER' as InfrastructureType,
};

/**
 * Convierte un ProcessedInfrastructure a NormalizedInfrastructure
 */
export function toNormalizedInfrastructure(
  infra: ProcessedInfrastructure
): NormalizedInfrastructure | null {
  // Solo incluir si tiene coordenadas finales
  if (!infra.xFinal || !infra.yFinal) {
    return null;
  }

  const mappedType = TYPE_MAP[infra.tipo] || ('OTHER' as InfrastructureType);

  // MAPCEN (13-sep-2026) · DOP-1=(a): la vía viaja al marcador como dato
  // semántico (`deriveProcedencia`, la misma de P1/P4/ficha de P2), no como
  // sufijo del pipeline; `hasCoords` es true porque arriba ya se filtró x/y.
  // `source` se copia además porque el popup ya tiene un bloque «Fuente»
  // que nunca se pintaba (HX-CAPTURA-MAXIMAL).
  const procedencia = deriveProcedencia(infra, true);

  return {
    id: infra.id,
    name: infra.nombre,
    type: mappedType,
    x: infra.xFinal,
    y: infra.yFinal,
    score: infra.score,
    address: infra.direccion,
    municipality: infra.municipio,
    province: infra.provincia,
    source: infra.source,
    procedencia,
    ...(procedencia === 'centroide' ? { aviso: avisoCentroide(infra) } : {}),
  };
}

/**
 * Convierte array de ProcessedInfrastructure a NormalizedInfrastructure[]
 * Filtra elementos sin coordenadas
 */
export function toNormalizedInfrastructures(
  infrastructures: ProcessedInfrastructure[]
): NormalizedInfrastructure[] {
  return infrastructures
    .map(toNormalizedInfrastructure)
    .filter((infra): infra is NormalizedInfrastructure => infra !== null);
}
